import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { BookOpenIcon, AwardIcon, MonitorIcon, ClipboardCheckIcon, BriefcaseIcon, LaptopIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { umaPrograms } from '../data/umaPrograms';
const umaEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}, {
  label: 'Graduate Degree',
  value: '8'
}];
const umaStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
/**
 * Resolves a program from the URL so a campaign link can skip the picker step.
 * Accepts ?program= (slug or route slug) or ?mProgram= (webhook code).
 */
function getProgramSlugFromUrl(): string {
  const params = new URLSearchParams(window.location.search);
  const requested = (params.get('program') || params.get('mProgram') || '').trim();
  if (!requested) return '';
  const match = umaPrograms.find(p => p.slug.toLowerCase() === requested.toLowerCase() || p.routeSlug.toLowerCase() === requested.toLowerCase() || p.mProgram.toLowerCase() === requested.toLowerCase());
  return match?.slug || '';
}
export function UMALandingPage() {
  usePageMeta('Ultimate Medical Academy Online | Healthcare Career Training Programs', 'Explore online healthcare degree programs at Ultimate Medical Academy. Get personalized support, financial aid options, and job search assistance. Request free info today.', {
    ogImage: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [preselectedProgram] = useState(getProgramSlugFromUrl);
  const [selectedProgram, setSelectedProgram] = useState(preselectedProgram);
  const [formStep, setFormStep] = useState(preselectedProgram ? 1 : 0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  // Fetch user IP on mount
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSlug = e.target.value;
    setSelectedProgram(newSlug);
    if (newSlug && formStep === 0) {
      setFormStep(1);
    }
  };
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      const currentProgram = umaPrograms.find(p => p.slug === selectedProgram);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/u7uetpg/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'Ultimate Medical Academy',
            logo: 'https://cdn.magicpatterns.com/uploads/ftg2brgTZFks7CVCNZ5jT1/UMA_LOGO_(1).webp',
            primaryColor: '#003B5C',
            accentColor: '#00BFA5',
            backUrl: '/colleges/ultimate-medical-academy',
            programTitle: currentProgram?.title || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#4ECDC4] focus:border-[#4ECDC4]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#4ECDC4] focus:border-[#4ECDC4]'}`;
  const currentProgramTitle = umaPrograms.find(p => p.slug === selectedProgram)?.title;
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-2334" data-mp="3ic8e1pyg78ux">
          <div data-id="element-2335" data-mp="52ftcc18nlk">
            <label htmlFor="hero-program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2336" data-mp="3hlnntmr6o8">
              Select a Program
            </label>
            <div className="relative" data-id="element-2337" data-mp="21vdxyznmgkk2">
              <select id="hero-program-select" value={selectedProgram} onChange={handleProgramChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#4ECDC4] focus:border-[#4ECDC4] font-medium text-gray-800" data-id="element-2338" data-mp="21wfirvufwlez">
                <option value="" data-id="element-2339" data-mp="1g15jk8cgea0h">-- Choose a Program --</option>
                {umaPrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-2340" data-mp="1kuhw3k3uyqcv">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-2341" data-mp="1pumcla4rnl33">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2342" data-mp="x3vyynve6qhn" />
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center" data-id="element-2343" data-mp="122yu9glklicf">
            Select a program above to get started
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-2344" data-mp="16zyydlyv0b0w">
          <div data-id="element-2345" data-mp="2dingu6uwwfcp">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2346" data-mp="2dixaq5j2v6no">
              Program
            </label>
            <div className="relative" data-id="element-2347" data-mp="6tvhrxijr9gb">
              <select id="step1-program" value={selectedProgram} onChange={handleProgramChange} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-2348" data-mp="6stw6z3wn4kc">
                {umaPrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-2349" data-mp="3oamtq6993k4c">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-2350" data-mp="3jcbo67pznvrx">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2351" data-mp="3f83vy0kbnxoy" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-2352" data-mp="3aau6ikwxnm14" />

          <div data-id="element-2353" data-mp="35calqnu2sgac">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2354" data-mp="35csom3b8tsr8">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-2355" data-mp="13juodmf4zxw9">
              <option value="" data-id="element-2356" data-mp="1oglx31vf8u4f">-- Please Select --</option>
              {umaStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-2357" data-mp="22gemmtv7p4at">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-2358" data-mp="1yg4h1ibc0fwh">{errors.startDate}</p>}
          </div>

          <div data-id="element-2359" data-mp="1aqa2mwbd6ja4">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2360" data-mp="1apqkbehsxsvk">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-2361" data-mp="1jic32ulfnzke">
              <option value="" data-id="element-2362" data-mp="qzwzxlhf2g1d">-- Please Select --</option>
              {umaEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-2363" data-mp="myl4v0frx2ls">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-2364" data-mp="hm6400qujqbp">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-2365" data-mp="2yl2ucdj816f">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2366" data-mp="2z35prup9dnb">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-2367" data-mp="vn8nc5qgimix">
              <option value="" data-id="element-2368" data-mp="lo9otfzeo79n">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-2369" data-mp="qn480j1abxua">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-2370" data-mp="36dz23okgnl08">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-2371" data-mp="3bduzv62keg7c">
            {!preselectedProgram && <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2372" data-mp="3baz2biumptjf">
                <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2373" data-mp="1841g1daipo6s" />
                Back
              </button>}
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-2374" data-mp="134ormo400923">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-2375" data-mp="p7s0eljywpko">
          <div data-id="element-2376" data-mp="k7nlv12me4x3">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2377" data-mp="k85oqgjsfhdz">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-2378" data-mp="2el50fyl1rrfz" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-2379" data-mp="30t0zongt40ai">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-2380" data-mp="235jtwficsjna">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-2381" data-mp="237mtnr9b5r57" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-2382" data-mp="2d24zb1yabwrn">
                <div data-id="element-2383" data-mp="2i2j5sfk8mynk">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2384" data-mp="2i3ubikx8ughb">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#4ECDC4] focus:border-[#4ECDC4] bg-gray-50" placeholder="City" data-id="element-2385" data-mp="3thpt00bmf1bp" />
                </div>
                <div data-id="element-2386" data-mp="4wmd6y2u39r3">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2387" data-mp="4x4jyon4h6d9">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#4ECDC4] focus:border-[#4ECDC4] bg-gray-50" placeholder="State" data-id="element-2388" data-mp="dw9dfgjtu9ik" />
                </div>
              </div>
              <div data-id="element-2389" data-mp="iospua6s2yls">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2390" data-mp="io9ifner7sm0">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#4ECDC4] focus:border-[#4ECDC4]'}`} placeholder="123 Main St" data-id="element-2391" data-mp="smypsm90xuo0" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-2392" data-mp="17a796achl6b2">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-2393" data-mp="xa6lydbhy61l">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2394" data-mp="xb87xcu1wcy3">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2395" data-mp="ofs9eiicp9m6" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-2396" data-mp="9mp2d52dye9p">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-2397" data-mp="3htvfk9wyi24q">
          <div className="grid grid-cols-2 gap-3" data-id="element-2398" data-mp="3huy1e3ff9eds">
            <div data-id="element-2399" data-mp="3cw3kj710lzwb">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2400" data-mp="3cwmspw0v59x5">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-2401" data-mp="9f4orzo7on20" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-2402" data-mp="3vl1jqjb6knxy">{errors.firstName}</p>}
            </div>
            <div data-id="element-2403" data-mp="2p697opgp4bc2">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2404" data-mp="2p6skjrrho9j8">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-2405" data-mp="zpdnwwc1dzle" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-2406" data-mp="19m09jnc6suoh">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-2407" data-mp="1eejn4kavjpta">
            <div data-id="element-2408" data-mp="1jcuv0phlzq8v">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2409" data-mp="1jde7vrsejog1">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-2410" data-mp="1sdjcju67o8qi" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-2411" data-mp="3u55vr9jv2zcf">{errors.email}</p>}
            </div>
            <div data-id="element-2412" data-mp="2yl5vt5jlxgz">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2413" data-mp="2y36worhx55d">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-2414" data-mp="24tsjf9xi41v2" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-2415" data-mp="29mllfq1l4w3g">{errors.phone}</p>}
            </div>
          </div>

          <div className="space-y-2 pt-2" data-id="element-2416" data-mp="1lyc2cvvramg0">
            <label className="flex items-start gap-2.5 cursor-pointer" data-id="element-2417" data-mp="1lz412fknh0ai">
              <input type="checkbox" checked={formData.tcpaConsent} onChange={e => updateFormData('tcpaConsent', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#00BFA5] focus:ring-[#00BFA5] cursor-pointer flex-shrink-0" data-id="element-2418" data-mp="1gzr4h33z8euq" />
              <span className="text-[10px] text-gray-500 leading-tight" data-id="element-2419" data-mp="37adlqsvxwdf9">
                By clicking the Request Info button, you agree to be contacted
                by phone, text message, or email via automated systems by
                Ultimate Medical Academy about your education at the phone
                numbers you provided above, including any wireless number(s).
                You are not obligated to agree to automated contact to enroll.
                Note that even non-automated calls are recorded for quality
                assurance.
              </span>
            </label>
            {errors.tcpaConsent && <p className="text-xs text-red-600" data-id="element-2420" data-mp="3smku0ig3kmcm">{errors.tcpaConsent}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-2421" data-mp="1fi2tr09eg2e">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2422" data-mp="1egondny5dgg">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2423" data-mp="ff9omdx0p0qy" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-2424" data-mp="k5ho02szbb0j">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-2425" data-mp="21z6mtjirzb0q" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-2426" data-mp="1t2p9c9mxv11t">
      {/* A. Custom Header */}
      <header className="bg-white py-4 px-6 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-2427" data-mp="1xx301yqnok05">
        <img src="https://cdn.magicpatterns.com/uploads/ftg2brgTZFks7CVCNZ5jT1/UMA_LOGO_(1).webp" alt="Ultimate Medical Academy Logo" className="h-16 object-contain" data-id="element-2428" data-mp="1j4j9ryolu704" />
      </header>

      {/* B. Hero Section */}
      <section className="bg-gradient-to-r from-[#176B9C] to-[#003B5C] relative overflow-hidden" data-id="element-2429" data-mp="3if0ut87smyan">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-2430" data-mp="16rnx33vtrgrx" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 flex flex-col lg:flex-row items-center gap-10 lg:gap-16 relative z-10" data-id="element-2431" data-mp="1bra7z6o6kkxk">
          {/* Left Content */}
          <div className="flex-1 text-white max-w-xl" data-id="element-2432" data-mp="1pk9l520hq31y">
            <p className="text-[#4ECDC4] font-medium mb-3 text-lg tracking-wide" data-id="element-2433" data-mp="1lg1xz99okv5u">
              Ultimate Medical Academy Online<sup className="text-xs" data-id="element-2434" data-mp="1zfti7rqsj7sa">1</sup>
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] mb-6 text-white tracking-tight" data-id="element-2435" data-mp="1uh8g1mrzae17">
              {umaPrograms.find(p => p.slug === selectedProgram)?.shortTitle || <>
                  Healthcare
                  <br data-id="element-2436" data-mp="5bl3jubuq1e" />
                  Career Training
                </>}
            </h1>
            <p className="text-lg text-white/85 max-w-lg leading-relaxed" data-id="element-2437" data-mp="207b5vm41kpfp">
              UMA students get personalized attention throughout the entire
              education journey. Our students get support from the very
              beginning, throughout career training, and after graduation.
            </p>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-8 sm:p-10 flex-shrink-0 relative z-10" data-id="element-2438" data-mp="2j549kqmhp3e5">
            <form onSubmit={e => e.preventDefault()} id="uma-lead-form" data-id="element-2439" data-mp="2o1ktt9qkixly">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" value="" data-id="element-2440" data-mp="2wvr74fgikn8n" />
              <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-2441" data-mp="31vc5annv0rkb" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#003B5C] mb-4" data-id="element-2442" data-mp="31t991lw46275">
                Learn how we can help you succeed.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-2443" data-mp="3gw0uuewrp9nw">
                <div className="bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-2444" data-mp="3bxpnqbxuxd9d"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-2445" data-mp="3j7ggcfqq1qt">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2446" data-mp="3hw59rjytrpo">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>
        </div>
      </section>

      {/* C. Benefits Icons Row */}
      <section className="bg-[#F4F9FB] py-12 border-b border-gray-200" data-id="element-2447" data-mp="hgvvv4r1ggoh">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2448" data-mp="1ze0gjn5iv2x">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 text-center" data-id="element-2449" data-mp="70bsdnxyr3hm">
            {[{
            icon: BookOpenIcon,
            title: 'One-on-one\nAcademic Support'
          }, {
            icon: AwardIcon,
            title: 'Certification Prep²',
            sup: '2'
          }, {
            icon: MonitorIcon,
            title: '24/7 Technical\nSupport'
          }, {
            icon: ClipboardCheckIcon,
            title: "Apply Degree Credits\nToward a Bachelor's³",
            sup: '3'
          }, {
            icon: BriefcaseIcon,
            title: 'Job Search\nAssistance'
          }, {
            icon: LaptopIcon,
            title: 'Laptops for those\nwho qualify⁴',
            sup: '4'
          }].map((item, idx) => <div key={idx} className="flex flex-col items-center" data-id="element-2450" data-mp="13ohrxwg135p1" data-mpi={idx}>
                <item.icon className="w-10 h-10 text-[#176B9C] mb-3 stroke-[1.5]" data-id="element-2451" data-mp="18ilzohx4bhr6" />
                <p className="text-xs font-bold text-[#176B9C] whitespace-pre-line leading-tight" data-id="element-2452" data-mp="2t6epw2x02k1n">
                  {item.title.replace(/²|³|⁴/g, '')}
                  {item.sup && <sup className="text-[10px]" data-id="element-2453" data-mp="32wztk4rj6erf">{item.sup}</sup>}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* D. Three Feature Cards */}
      <section className="py-16 bg-white" data-id="element-2454" data-mp="2y0ixmoe3aw3s">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2455" data-mp="2a9n5tr9mw0wj">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-2456" data-mp="259r27t4umbhi">
            {[{
            img: 'https://cdn.magicpatterns.com/uploads/dFP2Bkreu2BvfkWxRv6Va3/Image_1.webp',
            title: 'Financial Aid',
            desc: 'Let us help you explore options for financial aid. We will help you through the process to see if you qualify.'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/9FrwRXe1BYLuSp6GRYweVA/image_2.webp',
            title: 'Job Search Assistance',
            desc: 'Our Career Service team helps graduates pursue job opportunities, schedule interviews, and more.'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/qcGrjYfSLHXUMihbDJMyJz/image_3.webp',
            title: 'Engaging Online Learning',
            desc: 'Get individual instructor feedback, personalized advisor support, and interact with peers on discussion posts.'
          }].map((card, idx) => <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col" data-id="element-2457" data-mp="3f0c03zpr3zd4" data-mpi={idx}>
                <img src={card.img} alt={card.title} className="w-full h-48 object-cover" data-id="element-2458" data-mp="3jz5a3i5tx88j" />
                <div className="p-6 flex-1" data-id="element-2459" data-mp="3owyjsl6cckdk">
                  <h3 className="text-xl font-bold text-[#003B5C] mb-3" data-id="element-2460" data-mp="3togj74spucwf">
                    {card.title}
                  </h3>
                  <p className="text-sm text-[#176B9C] font-medium leading-relaxed" data-id="element-2461" data-mp="2w9uj2sea6s7l">
                    {card.desc}
                  </p>
                </div>
              </div>)}
          </div>
          <div className="text-center" data-id="element-2462" data-mp="1idb352ef7m69">
            <button onClick={scrollToForm} className="inline-block bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] text-white font-bold py-3 px-12 rounded-full hover:shadow-lg transition-all" data-id="element-2463" data-mp="1ic8cmvl0flqv">
              Take the Next Step
            </button>
          </div>
        </div>
      </section>

      {/* E. '4 out of 5' Stat Section */}
      <section className="bg-[#F4F9FB] py-16" data-id="element-2464" data-mp="ze5iy3hip0mq">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-center gap-12" data-id="element-2465" data-mp="zeyqv7s7pw6g">
          <div className="text-center md:text-right max-w-xs" data-id="element-2466" data-mp="ulcc9a2hni80">
            <div className="text-[#00BFA5] font-bold text-5xl sm:text-6xl mb-2" data-id="element-2467" data-mp="qmwtjidfww0e">
              4 <span className="text-[#176B9C]" data-id="element-2468" data-mp="lnjxc70oicl5">out of 5</span>
            </div>
            <p className="text-[#176B9C] text-sm font-medium" data-id="element-2469" data-mp="gnedhd0nhudm">
              surveyed employers say they are likely to hire a UMA graduate
              again.⁵
            </p>
          </div>
          <div className="flex gap-2 sm:gap-4" data-id="element-2470" data-mp="20is935c67wox">
            {[1, 2, 3, 4].map(i => <div key={i} className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-[#D8E8F0] flex items-center justify-center bg-white" data-id="element-2471" data-mp="20jjxnw7cou5p">
                <CheckCircle2Icon className="w-10 h-10 sm:w-12 sm:h-12 text-[#00BFA5]" data-id="element-2472" data-mp="1ql43fkd114rz" />
              </div>)}
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-[#D8E8F0] flex items-center justify-center bg-transparent" data-id="element-2473" data-mp="1viw4p4zka38u">
              <CheckCircle2Icon className="w-10 h-10 sm:w-12 sm:h-12 text-[#D8E8F0]" data-id="element-2474" data-mp="1gsokoktwoc61" />
            </div>
          </div>
        </div>
      </section>

      {/* F. 25% Georgetown Study Section */}
      <section className="py-16 bg-white" data-id="element-2475" data-mp="17vxrcg6qnbf5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2476" data-mp="1cso6n1apy2zi">
          <div className="bg-[#003B5C] rounded-2xl overflow-hidden relative flex flex-col md:flex-row items-center shadow-xl" data-id="element-2477" data-mp="1cr38z2t3y3xf">
            <div className="p-10 md:p-16 flex-1 z-10 flex items-center gap-8" data-id="element-2478" data-mp="12uqjkh4lic8h">
              <div className="text-6xl sm:text-8xl font-bold text-white" data-id="element-2479" data-mp="12wuon2e8dhac">
                25<span className="text-4xl sm:text-6xl" data-id="element-2480" data-mp="j9hy4evr2n9q">%</span>
              </div>
              <div className="w-1 h-24 bg-[#00BFA5] hidden sm:block" data-id="element-2481" data-mp="f8xz15rm99ky"></div>
              <p className="text-white text-lg sm:text-xl font-medium max-w-sm leading-snug" data-id="element-2482" data-mp="t7nygs1wrlch">
                A Georgetown study found that an associate degree can increase
                lifetime earnings over a high school diploma by 25 percent.⁶
              </p>
            </div>
            <div className="w-full md:w-1/2 h-64 md:h-full absolute right-0 top-0 opacity-80 md:opacity-100" data-id="element-2483" data-mp="y70lpdwoyufl">
              <img src="https://cdn.magicpatterns.com/uploads/xs9nMQM6BrsxQ4Td97ACQ9/image.png" alt="Graduate smiling" className="w-full h-full object-cover object-right" data-id="element-2484" data-mp="1by5crrlzueeb" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#003B5C] to-transparent" data-id="element-2485" data-mp="1lw326iflah8i"></div>
            </div>
          </div>
        </div>
      </section>

      {/* G. Testimonial Section */}
      <section className="bg-[#176B9C] py-16 relative" data-id="element-2486" data-mp="2iborqnajtggg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2487" data-mp="2ib5cjeca9664">
          <div className="text-[#00BFA5] text-6xl font-serif leading-none mb-4" data-id="element-2488" data-mp="2ddndqgrzc7bu">
            "
          </div>
          <p className="text-white text-xl sm:text-2xl font-medium leading-relaxed mb-8" data-id="element-2489" data-mp="299nw1uuyr2gw">
            "I'm a working mom and mother of four. I am thankful to UMA for the
            flexibility, so I am able to complete my courses and pass with
            honors!"
          </p>
          <div className="flex items-center justify-center gap-4" data-id="element-2490" data-mp="1ulug291ytxxs">
            <div className="text-right" data-id="element-2491" data-mp="1pmzs6sp55j6t">
              <div className="text-white font-bold text-lg" data-id="element-2492" data-mp="1po1ani8hl7yk">Merelesita R.</div>
              <div className="text-white/80 text-sm" data-id="element-2493" data-mp="1llxy0qa2zxbp">UMA Graduate</div>
            </div>
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white/20" data-id="element-2494" data-mp="1gm1sunpndzuk">
              <img src="https://cdn.magicpatterns.com/uploads/xs9nMQM6BrsxQ4Td97ACQ9/image.png" alt="Merelesita R." className="w-full h-full object-cover" data-id="element-2495" data-mp="1bnia0w6cp46f" />
            </div>
          </div>
        </div>
        {/* Triangle pointer */}
        <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[16px] border-l-transparent border-r-[16px] border-r-transparent border-t-[16px] border-t-[#176B9C]" data-id="element-2496" data-mp="3s8dhuu82p6yq"></div>
      </section>

      {/* H. Accreditation Bar */}
      <section className="bg-[#F8F9FA] py-12 mt-4" data-id="element-2497" data-mp="34k2p6ecj6dlj">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center gap-6" data-id="element-2498" data-mp="34ldtcf9w1nd6">
          <div className="w-20 h-20 flex-shrink-0 bg-white rounded-full p-2 shadow-sm flex items-center justify-center border border-gray-100" data-id="element-2499" data-mp="39jf9arc0p6kf">
            <AwardIcon className="w-10 h-10 text-[#003B5C]" data-id="element-2500" data-mp="3ib8voue89bhx" />
          </div>
          <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2501" data-mp="3idbv2519sgzb">
            UMA is institutionally accredited by the Accrediting Bureau of
            Health Education Schools (ABHES.org), a national accrediting agency
            that is recognized by the U.S. Secretary of Education. Ultimate
            Medical Academy does not recruit or enroll residents of the European
            Union.
          </p>
        </div>
      </section>

      {/* I. Citations Section */}
      <section className="bg-[#F8F9FA] py-8 border-t border-gray-200" data-id="element-2502" data-mp="6pzhwppcnwa8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2503" data-mp="1sh8yzbc1h28">
          <h4 className="text-xs font-bold text-gray-800 mb-3" data-id="element-2504" data-mp="3a77xf2wluk1s">Citations</h4>
          <ol className="text-[10px] text-gray-500 space-y-1 list-decimal list-inside" data-id="element-2505" data-mp="3a9c4ftpsvz6a">
            <li data-id="element-2506" data-mp="359zfn3fdci12">
              Coursework is online except for on-site externship/professional
              practice experience. Set schedules required for on-site
              externship/professional practice experience courses in select
              programs and coursework deadlines are set by instructors.
            </li>
            <li data-id="element-2507" data-mp="ug3lix2jfw18">
              Learners must meet eligibility criteria to sit for applicable
              certification exams
            </li>
            <li data-id="element-2508" data-mp="11ytfphcs3kfy">
              Tuition considerations and credit transfer decisions are at the
              discretion of the receiving institution.
            </li>
            <li data-id="element-2509" data-mp="s1yid1s8rq39">
              For full information on laptop program eligibility, please see the
              UMA Catalog.
            </li>
            <li data-id="element-2510" data-mp="x11n0l4b7uap">
              Source: Survey was conducted by Hanover Research. Employers that
              hired UMA Online students between Jul 1, 2022 - September 30,
              2023. Survey responses received from 257 employers. Excludes
              corporate alliance partners.
            </li>
            <li data-id="element-2511" data-mp="1b011izy3yst5">
              Georgetown University. The College Payoff. Study found that an
              associate degree can increase lifetime earnings over a high school
              diploma by 25 percent.
            </li>
          </ol>
          <p className="text-[10px] text-gray-500 mt-4" data-id="element-2512" data-mp="28feyeqhvmh9u">
            Where national data regarding earnings or employability within a
            particular field are cited, individual readers should take note that
            national averages may not accurately reflect the earnings of workers
            in their particular part of the country, and may include earners at
            all stages of their career, not just entry-level wages for recent
            graduates.
          </p>
          <p className="text-[10px] text-gray-500 mt-2" data-id="element-2513" data-mp="13huzpy7uyrd1">
            Set schedules required for on-site externship / professional
            practice experience courses in select programs and in all programs,
            coursework deadlines are set by instructors.
          </p>
        </div>
      </section>

      {/* J. UMA Footer */}
      <footer className="bg-white py-12 border-t border-gray-200" data-id="element-2514" data-mp="orxbxjduc3od">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center md:items-start gap-8" data-id="element-2515" data-mp="1m82r5j273tde">
          <img src="https://cdn.magicpatterns.com/uploads/ftg2brgTZFks7CVCNZ5jT1/UMA_LOGO_(1).webp" alt="Ultimate Medical Academy Logo" className="h-14 object-contain" data-id="element-2516" data-mp="1hgkzjlm2b553" />
          <div className="text-center md:text-left text-xs text-gray-600" data-id="element-2517" data-mp="1trlokuc6rp5i">
            <p className="mb-1" data-id="element-2518" data-mp="1ttoqocqm58ql">
              © 2026 UMA Education, Inc. d/b/a Ultimate Medical Academy. All
              rights reserved.
            </p>
            <p className="mb-4" data-id="element-2519" data-mp="23r2wzjitoh56">
              A member of the Vocate Education Solutions System
            </p>
            <div className="flex items-center justify-center md:justify-start gap-2 text-[#176B9C]" data-id="element-2520" data-mp="28omjf6poc087">
              <a href="#" className="hover:underline" data-id="element-2521" data-mp="2cp6gybe5t5uv">
                Marketing Statistics
              </a>
              <span data-id="element-2522" data-mp="2hi7vujed95eg">|</span>
              <a href="#" className="hover:underline" data-id="element-2523" data-mp="2mgj62v8kphx7">
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}